import React from 'react';
import { Routes, Route, Navigate, useLocation } from 'react-router-dom';
import { useAuth } from './context/AuthContext';
import { useSettings } from './context/SettingsContext';
import Sidebar from './components/Sidebar';
import BottomNav from './components/BottomNav';
import Login from './pages/Login';
import ForgotPassword from './pages/ForgotPassword';
import LegalTerms from './pages/LegalTerms';
import PaymentResult from './pages/PaymentResult';
import Register from './pages/Register';
import Signals from './pages/Signals';
import Subscribe from './pages/Subscribe';
import News from './pages/News';
import Market from './pages/Market';
import StockPrice from './pages/StockPrice';
import AiSignals from './pages/AiSignals';
import EarningsCalendar from './pages/EarningsCalendar';
import Help from './pages/Help';
import Education from './pages/Education';
import ReferFriend from './pages/ReferFriend';
import AdminPerformance from './pages/admin/AdminPerformance';
import AdminSignalHistory from './pages/admin/AdminSignalHistory';
import AdminRevenue from './pages/admin/AdminRevenue';
import AdminMessages from './pages/admin/AdminMessages';
import AdminDashboard from './pages/admin/AdminDashboard';
import AdminUsers from './pages/admin/AdminUsers';
import AdminSignals from './pages/admin/AdminSignals';
import AdminMarket from './pages/admin/AdminMarket';
import RateApp from './pages/RateApp';
import DeleteAccount from './pages/DeleteAccount';
import AdminRatings from './pages/admin/AdminRatings';
import AdminPricing from './pages/admin/AdminPricing';
import SectorRotation from './pages/SectorRotation';
import Notifications from './pages/Notifications';
import AdminNotifications from './pages/admin/AdminNotifications';

/** Catches render-time JS errors (e.g. a bad API response shape crashing a
 *  .map()) that would otherwise unmount the whole tree and leave a blank
 *  screen with no clue why — most visible on the Android/Capacitor build,
 *  which has no visible console. Shows the error message + a reload button
 *  instead of nothing. */
class ErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { error: null };
  }
  static getDerivedStateFromError(error) {
    return { error };
  }
  componentDidCatch(error, info) {
    console.error('Render error caught by ErrorBoundary:', error, info);
  }
  render() {
    if (this.state.error) {
      return (
        <div className="min-h-screen flex items-center justify-center bg-slate-50 p-6">
          <div className="max-w-md w-full rounded-2xl border border-rose-200 bg-white shadow-sm p-5">
            <h1 className="text-lg font-bold text-rose-600 mb-2">Something went wrong</h1>
            <p className="text-sm text-slate-600 mb-3">
              {this.state.error?.message || String(this.state.error)}
            </p>
            <button
              onClick={() => { this.setState({ error: null }); window.location.href = '/signals'; }}
              className="text-sm bg-slate-900 text-white px-4 py-2 rounded-lg"
            >
              Reload
            </button>
          </div>
        </div>
      );
    }
    return this.props.children;
  }
}

function PrivateRoute({ children, adminOnly = false, semiOrAdmin = false, allowUnpaid = false }) {
  const { user, loading } = useAuth();
  const loc = useLocation();
  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[60vh] text-slate-400 text-sm">
        Loading…
      </div>
    );
  }
  if (!user) return <Navigate to="/login" />;
  if (adminOnly && user.role !== 'ROLE_ADMIN') return <Navigate to="/signals" />;
  if (semiOrAdmin && user.role !== 'ROLE_ADMIN' && user.role !== 'ROLE_SEMI_ADMIN') {
    return <Navigate to="/signals" />;
  }
  // After 7-day trial (or unpaid): force regular users to activate / pay
  const isStaff = user.role === 'ROLE_ADMIN' || user.role === 'ROLE_SEMI_ADMIN';
  const mustPay = !isStaff && (user.needsPayment || user.subscriptionActive === false);
  if (mustPay && !allowUnpaid) {
    return <Navigate to="/subscribe" replace state={{ reason: 'subscription_expired' }} />;
  }
  return children;
}

/** Market page is admin-toggleable for regular users (Admin home → Site settings) —
 *  a bookmarked/typed /market URL goes to Signals instead of an empty page. */
function MarketGate({ children }) {
  const { marketPageEnabled } = useSettings();
  if (!marketPageEnabled) return <Navigate to="/signals" replace />;
  return children;
}

function AppShell({ children }) {
  const { user } = useAuth();
  const loc = useLocation();
  const authOnlyPaths = ['/login', '/register'];
  const isAuthPage = authOnlyPaths.includes(loc.pathname);

  if (!user || isAuthPage) {
    return (
      <div className="min-h-screen bg-slate-50 text-slate-900 antialiased">
        <main className="container mx-auto px-4 py-6 max-w-5xl">
          <ErrorBoundary>{children}</ErrorBoundary>
        </main>
      </div>
    );
  }

  // Phone bottom tab bar — not for accounts stuck on the paywall (they can only use
  // Subscribe / Terms, same rule as the header menu).
  const isStaff = user.role === 'ROLE_ADMIN' || user.role === 'ROLE_SEMI_ADMIN';
  const mustPay = !isStaff && (user.needsPayment || user.subscriptionActive === false);
  const showBottomNav = !mustPay;

  return (
    <div className="min-h-screen bg-slate-50 text-slate-900 antialiased">
      <ErrorBoundary><Sidebar /></ErrorBoundary>
      {/* Extra bottom padding on phones so the last card isn't hidden behind the
          bottom tab bar (+ the Android system bar inset). */}
      <main
        className={`container mx-auto px-4 py-6 max-w-5xl ${showBottomNav ? 'max-sm:pb-[calc(5rem+var(--safe-area-inset-bottom,env(safe-area-inset-bottom,0px)))]' : ''}`}
      >
        <ErrorBoundary>{children}</ErrorBoundary>
      </main>
      {showBottomNav && <ErrorBoundary><BottomNav /></ErrorBoundary>}
    </div>
  );
}

export default function App() {
  return (
    <AppShell>
      <Routes>
        <Route path="/login" element={<Login />} />
        <Route path="/forgot-password" element={<ForgotPassword />} />
        <Route path="/legal" element={<LegalTerms />} />
        {/* Stripe return page — public (see PaymentResult) */}
        <Route path="/payment/result" element={<PaymentResult />} />
        {/* legal stays public */}
        <Route path="/register" element={<Register />} />
        <Route path="/" element={<Navigate to="/signals" replace />} />
        {/* Old account "Dashboard" page removed — Signals is the home page for everyone. */}
        <Route path="/dashboard" element={<Navigate to="/signals" replace />} />
        <Route path="/signals" element={<PrivateRoute><Signals /></PrivateRoute>} />
        <Route path="/market" element={<PrivateRoute><MarketGate><Market /></MarketGate></PrivateRoute>} />
        <Route path="/sector-rotation" element={<PrivateRoute><SectorRotation /></PrivateRoute>} />
        <Route path="/ai-signals" element={<PrivateRoute><AiSignals /></PrivateRoute>} />
        {/* AI Rapid Profit merged into /signals (unified Signals page), and its admin
            review desk merged into /admin/signals (unified Manage Signals inbox) —
            redirect any old bookmarks/links rather than 404ing. */}
        <Route path="/weekly-signals" element={<Navigate to="/signals" replace />} />
        <Route path="/admin/weekly-signals" element={<Navigate to="/admin/signals" replace />} />
        <Route path="/news" element={<PrivateRoute><News /></PrivateRoute>} />
        <Route path="/earnings" element={<PrivateRoute><EarningsCalendar /></PrivateRoute>} />
        <Route path="/help" element={<PrivateRoute><Help /></PrivateRoute>} />
        <Route path="/education" element={<PrivateRoute><Education /></PrivateRoute>} />
        <Route path="/refer" element={<PrivateRoute><ReferFriend /></PrivateRoute>} />
        <Route path="/rate" element={<PrivateRoute><RateApp /></PrivateRoute>} />
        <Route path="/delete-account" element={<PrivateRoute><DeleteAccount /></PrivateRoute>} />
        <Route path="/admin/ratings" element={<PrivateRoute adminOnly><AdminRatings /></PrivateRoute>} />
        <Route path="/stock/:symbol" element={<PrivateRoute><StockPrice /></PrivateRoute>} />
        <Route path="/admin/performance" element={<PrivateRoute adminOnly><AdminPerformance /></PrivateRoute>} />
        <Route path="/admin/signal-history" element={<PrivateRoute adminOnly><AdminSignalHistory /></PrivateRoute>} />
        <Route path="/admin/revenue" element={<PrivateRoute adminOnly><AdminRevenue /></PrivateRoute>} />
        <Route path="/admin/messages" element={<PrivateRoute adminOnly><AdminMessages /></PrivateRoute>} />
        <Route path="/stock-price" element={<PrivateRoute><StockPrice /></PrivateRoute>} />
        <Route path="/subscribe" element={<PrivateRoute allowUnpaid><Subscribe /></PrivateRoute>} />
        <Route path="/admin" element={<PrivateRoute adminOnly><AdminDashboard /></PrivateRoute>} />
        <Route path="/admin/users" element={<PrivateRoute adminOnly><AdminUsers /></PrivateRoute>} />
        <Route path="/admin/signals" element={<PrivateRoute semiOrAdmin><AdminSignals /></PrivateRoute>} />
        <Route path="/admin/market" element={<PrivateRoute adminOnly><AdminMarket /></PrivateRoute>} />
        <Route path="/admin/pricing" element={<PrivateRoute adminOnly><AdminPricing /></PrivateRoute>} />
        <Route path="/notifications" element={<PrivateRoute><Notifications /></PrivateRoute>} />
        <Route path="/admin/notifications" element={<PrivateRoute semiOrAdmin><AdminNotifications /></PrivateRoute>} />
      </Routes>
    </AppShell>
  );
}
